/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { globalLoader, globalAlert } from "../actions/commonActions";
// import {
//   lmsGetCourseApi,
//   lmsCourseReadinessApi,
//   lmsUpdateCourseStatusApi,
//   lmsCourseReportApi,
//   lmsCertificatesApi,
// } from "./_lmsActions";

import { learnerCourseDetailApi } from "../lms/_learnerActions";
import { distinctAssetFileName, parseCourseDetail } from "./_lmsHelpers";

const moduleSections = (module) => module?.sections || [];
const formatWatchTime = (seconds = 0, t = (key, fallback) => fallback) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const secondsShort = t("learner.lms.content.secondsShort", "sec");
  const minutesShort = t("learner.lms.content.minutesShort", "min");
  const hourShort = t("learner.lms.content.hourShort", "hr");
  const hoursShort = t("learner.lms.content.hoursShort", "hrs");
  if (totalSeconds <= 0) return `0 ${secondsShort}`;
  if (totalSeconds < 60) return `${totalSeconds} ${secondsShort}`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) {
    return `${totalMinutes} ${minutesShort}${remainingSeconds ? ` ${remainingSeconds} ${secondsShort}` : ""}`;
  }
  return `${hours} ${hours === 1 ? hourShort : hoursShort}${minutes ? ` ${minutes} ${minutesShort}` : ""}${remainingSeconds ? ` ${remainingSeconds} ${secondsShort}` : ""}`;
};

const contentNodeIcon = (node) => {
  if (node?.type === "course") return "fas fa-book-open";
  if (node?.type === "module") return "fas fa-folder";
  if (node?.type === "course_intro" || node?.type === "module_intro") return "fas fa-video";
  if (node?.type === "parts") return "fas fa-layer-group";
  if (node?.type === "part") return "fas fa-list-ul";
  if (node?.type === "quiz") return "fas fa-question-circle";
  if (node?.type === "completion") return "fas fa-flag-checkered";
  if (node?.type === "support") return "fas fa-life-ring";
  if (node?.assetType === "video") return "fas fa-video";
  if (node?.assetType === "pdf") return "fas fa-file-pdf";
  if (node?.assetType === "image") return "fas fa-image";
  return "fas fa-file-alt";
};
const contentAssetTypeLabel = (type, t = (key, fallback) => fallback) => {
  const normalized = String(type || "").toLowerCase();
  if (normalized === "pdf") return "PDF";
  if (normalized === "rich_text") return t("learner.lms.content.text", "Text");
  if (normalized === "video") return t("learner.lms.content.video", "Video");
  if (normalized === "image") return t("learner.lms.content.image", "Image");
  if (!normalized) return "";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};
const contentAssetLabel = (asset, labelPrefix = "Content", t = (key, fallback) => fallback) => {
  const typeLabel = contentAssetTypeLabel(asset?.type, t);
  const prefix = String(labelPrefix || "Content").trim();
  if (!typeLabel) return prefix;
  return typeLabel === "PDF" ? `${prefix} PDF` : `${prefix} ${typeLabel.toLowerCase()}`;
};
const assetFileName = (asset = {}) => {
  const explicitName = asset?.filename || asset?.fileName;
  if (explicitName) return String(explicitName);
  const source = asset?.title || asset?.url || "";
  const cleanSource = String(source).split("?")[0];
  const name = cleanSource.split("/").filter(Boolean).pop() || source;
  try {
    return decodeURIComponent(name);
  } catch (error) {
    return name;
  }
};

const countLabel = (count, oneKey, manyKey, fallbackOne, fallbackMany, t) =>
  t(count === 1 ? oneKey : manyKey, count === 1 ? fallbackOne : fallbackMany, { count });

const contentNodeMeta = (node, t) =>
  [
    node?.moduleCount ? countLabel(node.moduleCount, "learner.lms.content.moduleCountOne", "learner.lms.content.moduleCount", "{{count}} module", "{{count}} modules", t) : null,
    node?.sectionCount ? countLabel(node.sectionCount, "learner.lms.content.partCountOne", "learner.lms.content.partCount", "{{count}} part", "{{count}} parts", t) : null,
    node?.count ? countLabel(node.count, "learner.lms.content.partCountOne", "learner.lms.content.partCount", "{{count}} part", "{{count}} parts", t) : null,
    node?.serviceAudience,
    node?.serviceArea,
    contentAssetTypeLabel(node?.assetType, t),
    node?.durationSeconds ? formatWatchTime(node.durationSeconds, t) : null,
    node?.questionCount ? countLabel(node.questionCount, "learner.lms.content.questionCountOne", "learner.lms.content.questionCount", "{{count}} question", "{{count}} questions", t) : null,
  ]
    .filter(Boolean)
    .join(" • ");
const contentNodeDefaultLabel = (node, t = (key, fallback) => fallback) => {
  if (node?.type === "course") return t("learner.lms.content.courseName", "Course name");
  if (node?.type === "module") return t("learner.lms.content.moduleName", "Module name");
  return "";
};

const contentNodeLabel = (node, t) => {
  if (node?.type === "course") return t("learner.lms.content.courseName", "Course name");
  if (node?.type === "module") return t("learner.lms.content.moduleName", "Module name");
  if (node?.type === "quiz" && node?.label) return t("learner.lms.content.sectionQuiz", "Section quiz");
  return node?.label || "";
};

const contentNodeTitle = (node, t) => {
  const title = node?.title || "";
  if (node?.type === "course" && /^course contents$/i.test(title)) {
    return t("learner.lms.content.courseContents", "Course contents");
  }
  if ((node?.type === "course_intro" || node?.type === "module_intro") && /^course introduction$/i.test(title)) {
    return t("learner.lms.player.courseIntro", "Course introduction");
  }
  if (node?.type === "parts" && /^parts$/i.test(title)) {
    return t("learner.lms.content.parts", "Parts");
  }
  if (node?.type === "quiz" && /^section quiz$/i.test(title)) {
    return t("learner.lms.content.sectionQuiz", "Section quiz");
  }
  if (/^module \d+$/i.test(title)) {
    const n = Number(title.match(/\d+/)?.[0] || 0);
    return t("learner.lms.player.moduleNum", "Module {{n}}", { n });
  }
  if (/^part \d+$/i.test(title)) {
    const n = Number(title.match(/\d+/)?.[0] || 0);
    return t("learner.lms.player.partN", "Part {{n}}", { n });
  }
  if (/^asset \d+$/i.test(title)) {
    const n = Number(title.match(/\d+/)?.[0] || 0);
    return t("learner.lms.content.assetN", "Asset {{n}}", { n });
  }
  if (/^untitled$/i.test(title)) return t("learner.lms.content.untitled", "Untitled");
  return title || t("learner.lms.content.untitled", "Untitled");
};

const fallbackContentIndex = (course, modules = []) => {
  console.log("fallback Index------>",course)
  console.log("fallback modules---->",modules);
  const introChildren = (course?.introAssets || []).map((item, index) => ({
    id: item?._id || `${item?.serviceAudience || "intro"}-${index}`,
    type: "asset",
    assetType: item?.asset?.type,
    label: contentAssetLabel(item?.asset, `${item?.serviceAudience || "Course"} introduction`),
    title: item?.asset?.title || assetFileName(item?.asset) || `${item?.serviceAudience || "Course"} intro`,
    fileName: assetFileName(item?.asset),
    serviceAudience: item?.serviceAudience,
    durationSeconds: item?.asset?.durationSeconds || 0,
    status: item?.asset?.url || item?.asset?.content ? "ready" : "missing",
  }));
  return {
    summary: {
      modules: modules.length,
      parts: modules.reduce((sum, module) => sum + moduleSections(module).length, 0),
    },
    tree: [
      {
        id: "course-contents",
        type: "course",
        label: "Course name",
        title: course?.title || "Course contents",
        status: course?.status || "draft",
        moduleCount: modules.length,
        children: [
          {
            id: "course-intro",
            type: "course_intro",
            title: "Course introduction",
            status: introChildren.length ? "ready" : "missing",
            children: introChildren,
          },
          ...modules.map((module, moduleIndex) => {
            const sections = moduleSections(module);
            return {
              id: module?._id || module?.moduleId || `module-${moduleIndex}`,
              type: "module",
              label: "Module name",
              title: module?.title || `Module ${moduleIndex + 1}`,
              status: module?.isPublished ? "published" : "draft",
              sectionCount: sections.length,
              children: [
                {
                  id: `${module?._id || moduleIndex}-parts`,
                  type: "parts",
                  title: "Parts",
                  count: sections.length,
                  status: sections.length ? "ready" : "missing",
                  children: sections.map((section, sectionIndex) => ({
                    id: section?._id || `section-${moduleIndex}-${sectionIndex}`,
                    type: "part",
                    title: section?.title || `Part ${sectionIndex + 1}`,
                    status: (section?.assets || []).length ? "ready" : "missing",
                    children: [
                      ...(section?.assets || []).map((asset, assetIndex) => ({
                        id: asset?._id || `asset-${moduleIndex}-${sectionIndex}-${assetIndex}`,
                        type: "asset",
                        assetType: asset?.type,
                        label: contentAssetLabel(asset, assetIndex === 0 ? "Part" : `Part asset ${assetIndex + 1}`),
                        title: asset?.title || `Asset ${assetIndex + 1}`,
                        fileName: assetFileName(asset),
                        durationSeconds: asset?.durationSeconds || 0,
                        status: asset?.url || asset?.content ? "ready" : "missing",
                      })),
                      ...(section?.quizDoc || section?.quiz
                        ? [
                          {
                            id: section?.quizDoc?._id || `quiz-${moduleIndex}-${sectionIndex}`,
                            type: "quiz",
                            label: "Section quiz",
                            title: section?.quizDoc?.title || "Section quiz",
                            status: section?.quizDoc?.status || "draft",
                            questionCount: section?.quizDoc?.questions?.length || 0,
                          },
                        ]
                        : []),
                    ],
                  })),
                },
              ],
            };
          }),
        ],
      },
    ],
  };
  
};

const ContentTreeNode = ({ node, depth = 0 }) => {
  const { t } = useTranslation();
  const children = node?.children || [];
  const canCollapse = ["course", "module", "parts", "part"].includes(node?.type) && children.length > 0;
  const [isExpanded, setIsExpanded] = useState(false);
  const label = contentNodeLabel(node, t) || contentNodeDefaultLabel(node, t);
  const hasLabel = !!label;
  const value = contentNodeTitle(node, t);
  const rawFileName =
    node?.type === "asset"
      ? node?.fileName || ""
      : "";
  const fileName = distinctAssetFileName(value, rawFileName);
  return (
    <div className={`lms-content-node depth-${Math.min(depth, 3)} type-${node?.type || "item"}`}>
      <div className="lms-content-node-main">
        {canCollapse ? (
          <button
            type="button"
            className="lms-content-collapse-btn"
            aria-label={
              isExpanded
                ? t("learner.lms.content.collapseItem", "Collapse {{title}}", { title: value })
                : t("learner.lms.content.expandItem", "Expand {{title}}", { title: value })
            }
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((current) => !current)}
          >
            <i className={`fas ${isExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}></i>
          </button>
        ) : (
          <span className="lms-content-collapse-spacer"></span>
        )}
        <span className="lms-content-node-icon">
          <i className={contentNodeIcon(node)}></i>
        </span>
        <div className="lms-content-node-copy">
          {hasLabel && <span className="lms-content-node-label">{label}</span>}
          <strong>{value}</strong>
          {fileName && <em>{fileName}</em>}
          {contentNodeMeta(node, t) && <span>{contentNodeMeta(node, t)}</span>}
        </div>
      </div>
      {!!children.length && (!canCollapse || isExpanded) && (
        <div className="lms-content-children">
          {children.map((child, index) => (
            <ContentTreeNode
              node={child}
              depth={depth + 1}
              key={child?.id || `${node?.id || "node"}-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LearnerContent = ({ courseId }) => {
  const { t } = useTranslation();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [contentIndex, setContentIndex] = useState(null);


  const courseIdentifier = courseId;
  useEffect(() => {
    loadCourse(true);
  }, [courseIdentifier]);

  const loadCourse = async (first = false) => {
    try {
      globalLoader(true);
      const res = await learnerCourseDetailApi(courseIdentifier);
      if (res?.data?.status === 200) {
        const { course: c, modules: m, contentIndex: ci } = parseCourseDetail(res?.data?.data);
        setCourse(c);
        setModules(m || []);
        setContentIndex(ci || null);
      } else {
        globalAlert("error", res?.data?.message);
      }
      globalLoader(false);
    } catch (error) {
      globalLoader(false);
      globalAlert("error", error?.response?.data?.message);
    }
  };
  const courseContentIndex = contentIndex || fallbackContentIndex(course, modules);
  const contentSummary = courseContentIndex?.summary || {};
  const contentTree = courseContentIndex?.tree || [];
  return (
    <>

                <div className="container-fluid lms-course-detail-container">


                  <div className="lms-workspace-body">
                    {/* Contents */}
                    
                      <div className="card lms-form-card">
                        <div className="card-body">
                          <div className="lms-card-head">
                            <h3 className="lms-section-title mb-0">
                              <i className="fas fa-layer-group me-2"></i>
                              {t("learner.lms.content.courseContents", "Course contents")}
                            </h3>
                          </div>
                          <div className="lms-content-summary">
                            <span><strong>{contentSummary.modules || 0}</strong> {t("learner.lms.content.modules", "Modules")}</span>
                            {/* <span><strong>{contentSummary.parts || 0}</strong> {t("learner.lms.content.parts", "Parts")}</span>
                            <span><strong>{contentSummary.assets || 0}</strong> {t("learner.lms.content.assets", "Assets")}</span>
                            <span><strong>{contentSummary.quizzes || 0}</strong> {t("learner.lms.content.quizzes", "Quizzes")}</span> */}
                            <span><strong>{formatWatchTime(contentSummary.videoDurationSeconds || 0, t)}</strong> {t("learner.lms.content.video", "Video")}</span>
                          </div>
                          <div className="lms-content-tree">
                            {contentTree.length ? (
                              contentTree.map((node, index) => (
                                <ContentTreeNode
                                  node={node}
                                  key={node?.id || index}
                                />
                              ))
                            ) : (
                              <div className="lms-empty-inline">
                                {t("learner.lms.content.noModules", "No modules yet.")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                  </div>
                </div>
    </>
  );
};
export default LearnerContent;
